import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ticketManualsQuery } from '../api/easeedesk'
import { imageAnalysisQuery, searchManuals, type ManualHit } from '../api/easeedeskTicketTabs'
import { Spinner } from '../ui/Spinner'
import './TicketManuals.css'

/**
 * Fila "Manuals" - `easeedesk/tickets/image_analizer/index.php` cu scriptul
 * `image_analizer/index-1.js`.
 *
 * Numele minte putin: fila nu tine manuale, ci POZELE atasate tichetului, pe
 * care omul le trimite ca sa se afle ce aparat e in ele. Din poza se citeste
 * modelul (`analizeImage` - "AI Vision Analysis", prin serverul de descarcari),
 * si de la model se cauta manualul (`searchManuals` - safe-manuals.com si
 * manualslib.com, prin `Services\SearchManuals`).
 *
 * Asezarea e cea din portal: sus "Manual Device Search", cu casuta de cautare
 * pe jumatate de rand si rezultatele sub ea - fiecare o legatura care deschide
 * manualul intr-un cadru de 900px, ca `getManual1`; jos "Automated Device
 * Discovery", cate un `.media` pe poza, cu etichetele (primele trei, "show
 * more" pentru rest) si textele citite, fiecare text fiind si el o cautare de
 * manual care se deschide sub poza, ca `getManual`.
 */

export function TicketManuals({ id }: { id: number }) {
  const { data, error } = useQuery(ticketManualsQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div className="mn-wrap">
      <ManualSearch />

      {data.images.length === 0 ? (
        <div className="row">
          <div className="col-md-12">
            <div className="alert alert-info" role="alert">
              <i className="fas fa-info-circle" aria-hidden="true"></i> No pictures on this ticket.
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-md-12">
            <h4>Automated Device Discovery</h4>
            <br />
            {data.images.map((image) => (
              <ImageCard key={image.id} ticket={id} image={image} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** Manualul deschis: titlul si cadrul, ca `getManual` / `getManual1`. */
function ManualFrame({ hit, height }: { hit: ManualHit; height: number }) {
  return (
    <div className="manual-iframe-wrap">
      <h5>
        Manual: <strong>{hit.content}</strong>
      </h5>
      <br />
      <iframe id="manual-iframe" title={hit.content} src={hit.url} width="100%" style={{ border: 'none', height }}></iframe>
    </div>
  )
}

/** Rezultatele cautarii: legaturi cu `far fa-file`, sau "No result." - `makeSearch`. */
function Hits({ hits, onOpen }: { hits: ManualHit[]; onOpen(hit: ManualHit): void }) {
  if (hits.length === 0) return <div className="alert alert-info">No result.</div>

  return (
    <>
      {hits.map((hit, i) => (
        <a
          key={i}
          className="manual-frame"
          style={{ marginRight: 4 }}
          href={hit.url}
          onClick={(ev) => {
            ev.preventDefault()
            onOpen(hit)
          }}
        >
          <i className="far fa-file" aria-hidden="true"></i> {hit.content}
        </a>
      ))}
    </>
  )
}

function ManualSearch() {
  const [text, setText] = useState('')
  const [hits, setHits] = useState<ManualHit[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<ManualHit | null>(null)

  const search = () => {
    if (text.trim() === '') return
    setBusy(true)
    setOpen(null)
    searchManuals(text.trim())
      .then(setHits)
      .catch(() => setHits([]))
      .finally(() => setBusy(false))
  }

  return (
    <div className="row mn-search-block">
      <div className="col-md-12">
        <h4>Manual Device Search</h4>
        <br />
        <form
          id="searchCommand"
          className="form-horizontal"
          onSubmit={(ev) => {
            ev.preventDefault()
            search()
          }}
        >
          <div className="row">
            <div className="col-md-6 input-group">
              <span className="input-group-addon">
                <i className="fas fa-search" aria-hidden="true"></i>
              </span>
              <input type="text" placeholder="Search..." name="search" className="form-control input-sm" value={text} onChange={(ev) => setText(ev.target.value)} />
            </div>
          </div>
        </form>
      </div>
      <div className="col-md-12 mn-search-results" id="search-analizer-results">
        {busy ? <Spinner /> : hits !== null ? <Hits hits={hits} onOpen={setOpen} /> : null}
      </div>
      <div className="col-md-12" id="search-manual-iframe">
        {open && <ManualFrame hit={open} height={900} />}
      </div>
    </div>
  )
}

/** Cate etichete/texte se arata inainte de "show more" - `i <= 2` in script. */
const SHOWN = 3

function ImageCard({ ticket, image }: { ticket: number; image: { id: number; name: string; url: string } }) {
  const analysis = useQuery(imageAnalysisQuery(ticket, image.id))
  const [moreLabels, setMoreLabels] = useState(false)
  const [moreText, setMoreText] = useState(false)
  const [hits, setHits] = useState<ManualHit[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<ManualHit | null>(null)

  const lookUp = (text: string) => {
    setBusy(true)
    setOpen(null)
    searchManuals(text)
      .then(setHits)
      .catch(() => setHits([]))
      .finally(() => setBusy(false))
  }

  const ok = analysis.data?._meta?.status === 'OK'
  const labels = ok ? (analysis.data?.data?.labels ?? []) : []
  const texts = ok ? (analysis.data?.data?.text ?? []) : []

  return (
    <div className="media mn-card">
      <div className="media-left">
        {image.url === '' ? (
          <div className="mn-missing">
            <i className="fas fa-image" aria-hidden="true"></i>
          </div>
        ) : (
          <a href={image.url} target="_blank" rel="noopener">
            <img className="media-object mn-image" src={image.url} alt={image.name} loading="lazy" />
          </a>
        )}
      </div>
      <div className="media-body mn-body">
        <h5 className="media-heading">{image.name}</h5>
        <hr className="dotted" />
        <div className="analizer-results">
          {analysis.isPending ? (
            <Spinner />
          ) : !ok ? (
            <div className="alert alert-danger">Something went wrong. Please try again later.</div>
          ) : (
            <>
              {labels.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {(moreLabels ? labels : labels.slice(0, SHOWN)).map((label, i) => (
                    <span key={i} className="label label-primary" style={{ marginRight: 4 }}>
                      {label}
                    </span>
                  ))}
                  {labels.length > SHOWN && (
                    <span className="toggle-more" style={{ display: 'block', cursor: 'pointer', marginBottom: 5 }} onClick={() => setMoreLabels(!moreLabels)}>
                      <i className={`fa ${moreLabels ? 'fa-angle-up' : 'fa-angle-down'}`} aria-hidden="true"></i> {moreLabels ? 'show less' : 'show more'}
                    </span>
                  )}
                </div>
              )}
              {texts.length > 0 && (
                <div>
                  {(moreText ? texts : texts.slice(0, SHOWN)).map((text, i) => (
                    <a
                      key={i}
                      className="label label-success search-manual"
                      style={{ marginRight: 4, whiteSpace: 'pre-wrap' }}
                      href="#"
                      onClick={(ev) => {
                        ev.preventDefault()
                        lookUp(text)
                      }}
                    >
                      <i className="fas fa-search" aria-hidden="true"></i> {text}
                    </a>
                  ))}
                  {texts.length > SHOWN && (
                    <span className="toggle-more" style={{ display: 'block', cursor: 'pointer', marginBottom: 5 }} onClick={() => setMoreText(!moreText)}>
                      <i className={`fa ${moreText ? 'fa-angle-up' : 'fa-angle-down'}`} aria-hidden="true"></i> {moreText ? 'show less' : 'show more'}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="manuals-results">{busy ? <Spinner /> : hits !== null ? <Hits hits={hits} onOpen={setOpen} /> : null}</div>
      </div>
      <div className="manual-iframe">{open && <ManualFrame hit={open} height={800} />}</div>
    </div>
  )
}
